import React, { useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import LabelCDS from '@sharedCR/coreDesignSystem/LabelCDS';
import { LabelSize, LabelVariant } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
  AuditOperation,
  CHANGED_FIELDS_NONE,
  INITIAL_VALUES_LABEL,
} from '../../EntityAuditHistory.constants';
import { AuditRowDecorationPropType } from '../../EntityAuditHistory.propTypes';
import {
  decoratedFieldsCellStyles,
  fieldsCellStyles,
  mutedFieldStyles,
} from '../../EntityAuditHistory.styles';
import { EntityAuditHistoryTestIds } from '../../EntityAuditHistory.testIds';
import {
  createFieldMatcher,
  splitChangedFields,
  toOperation,
} from '../../EntityAuditHistory.utils';

const AuditFieldsCell = ({ summary, decoration = null }) => {
  const isMutedField = useMemo(
    () => createFieldMatcher(decoration?.mutedFields),
    [decoration?.mutedFields],
  );

  const { shown: shownFields, overflow: fieldsOverflow } = splitChangedFields(
    summary.changedFields,
  );
  const fieldsPlaceholder =
    toOperation(summary.operation) === AuditOperation.CREATED
      ? INITIAL_VALUES_LABEL
      : CHANGED_FIELDS_NONE;

  const fields = shownFields.length ? (
    <Box data-testid={EntityAuditHistoryTestIds.ROW_FIELDS} sx={fieldsCellStyles}>
      {shownFields.map(field => {
        const isMuted = isMutedField(field);

        return (
          <Box
            data-muted={isMuted}
            data-testid={EntityAuditHistoryTestIds.ROW_FIELD}
            key={field}
            sx={isMuted ? mutedFieldStyles : null}
          >
            <LabelCDS size={LabelSize.S} title={field} variant={LabelVariant.GRAY} />
          </Box>
        );
      })}
      {fieldsOverflow > 0 && (
        <Typography
          color="text.secondary"
          data-testid={EntityAuditHistoryTestIds.ROW_FIELDS_OVERFLOW}
          variant="regular12"
        >
          {`+${fieldsOverflow}`}
        </Typography>
      )}
    </Box>
  ) : (
    <Typography color="text.secondary" variant="regular14">
      {fieldsPlaceholder}
    </Typography>
  );

  if (_.isNil(decoration?.note)) {
    return fields;
  }

  return (
    <Box data-testid={EntityAuditHistoryTestIds.ROW_DECORATION} sx={decoratedFieldsCellStyles}>
      {fields}
      <Typography
        color="text.secondary"
        data-testid={EntityAuditHistoryTestIds.ROW_NOTE}
        variant="regular12"
      >
        {decoration.note}
      </Typography>
    </Box>
  );
};

AuditFieldsCell.propTypes = {
  summary: PropTypes.object.isRequired,
  decoration: AuditRowDecorationPropType,
};

export default AuditFieldsCell;
